import type { MarkerAppearance } from "../components/marker-appearances";
import { hasUsablePosition, type MapMarker, type MarkerScope, type MarkerSource, markerIdFor } from "../types";
import type { MarkerWarning } from "./resolve-marker-warning";

/**
 * What a projection supplies per document: the fields it read off the DTO, plus the appearance and
 * click it chose for that kind. Everything else about the marker is derived here.
 */
type ToMapMarkerParams = {
    source: MarkerSource;
    modelId: string;
    lat: number | null | undefined;
    long: number | null | undefined;
    appearance: MarkerAppearance;
    /** The device's name, as its tooltip and its group-menu row show it */
    label: string;
    warning: MarkerWarning | null;
    /** No default: a source has to say what its selection scope is rather than inherit one */
    scope: MarkerScope;
    onClick: () => void;
};

/**
 * Assembles a v2-drawn marker from what a projection read off one DTO.
 *
 * Every projection routes through here, so a new source cannot skip the position guard.
 *
 * Returns null when the document has no usable position, which the source drops.
 */
export const toMapMarker = ({
    source,
    modelId,
    lat,
    long,
    appearance,
    label,
    warning,
    scope,
    onClick,
}: ToMapMarkerParams): MapMarker | null => {
    if (!hasUsablePosition({ lat, long })) return null;

    return {
        id: markerIdFor(source, modelId),
        modelId,
        source,
        drawnBy: "v2",
        scope,
        lat: lat as number,
        long: long as number,
        appearance,
        badge: warning ? { variant: warning.variant } : null,
        tooltip: { label, warningText: warning?.text },
        onClick,
    };
};
